import { useState, useEffect, useMemo, useRef } from "react";
import { Box, Typography, TextField, MenuItem, Select, Button } from "@mui/material";
import { loadUserStats, useAdminData, userStatsKey } from "../../../services/adminDataStore";
import { FiDownload } from "react-icons/fi";

import DataTable from "datatables.net-react";
import DT from "datatables.net-dt";
import "datatables.net-dt/css/dataTables.dataTables.css";

DataTable.use(DT);

const CHART_STYLE = {
	backgroundColor: "#FFFFFF",
	borderRadius: "20px",
	border: "1px solid #EEF0F5",
	boxShadow: "0 2px 8px rgba(17,19,26,0.04)",
	width: "100%",
	minHeight: "100vh",
	boxSizing: "border-box",
};
const CARD_TITLE = { color: "#12131A", fontWeight: 700, fontSize: 17, mb: 0.5 };
const CARD_SUB = {
	color: "#8B93A1",
	display: "flex",
	alignItems: "center",
	gap: 0.75,
	fontWeight: 400,
	fontSize: 13,
	mb: 1.5,
};

function AllAccounts() {
	const [selectedCreator, setSelectedCreator] = useState("all");
	const [sortBy, setSortBy] = useState("signed_up_pacific");
	const [search, setSearch] = useState("");
	const tableRef = useRef(null);

	const { userStatsByKey, creatorList: creatorIdList } = useAdminData();
	const statsKey = userStatsKey(selectedCreator, sortBy);
	const users = userStatsByKey[statsKey] || [];
	const loading = !userStatsByKey[statsKey];

	useEffect(() => {
		loadUserStats(selectedCreator, sortBy);
	}, [selectedCreator, sortBy]);

	// Push our own search box into DataTables' built-in search
	useEffect(() => {
		const dt = tableRef.current?.dt();
		if (dt) dt.search(search).draw();
	}, [search]);

	const statusColor = (status) => {
		if (status === "active_48h") return "#4ade80";
		if (status === "inactive_48h") return "#fb7185";
		return "#6B7280";
	};

	const fmtDate = (iso) => {
		if (!iso) return "—";
		return new Date(iso).toLocaleString("en-US", {
			month: "short", day: "numeric", year: "numeric",
			hour: "2-digit", minute: "2-digit", hour12: false,
		});
	};

	const fmtMin = (v) => (v ? `${parseFloat(v).toFixed(1)}m` : "0m");

	const handleExportCsv = () => {
		const headers = ["Rank", "Cohort", "First Name", "Last Name", "Email", "Signed Up", "Status", "Pwa Install", "Subscribed",
			"Total Msgs", "Chat", "Voice", "Video", "Used Voice", "Used Video",
			"Voice Calls", "Video Calls", "Voice Min", "Video Min",
			"Msgs 24h", "Msgs 48h", "Msgs 7d", "Last Msg", "Last Call", "Tour Shown"];
		const rows = users.map((u) => [
			u.rank, u.cohort,
			u.first_name || "", u.last_name || "", u.email || "",
			u.signed_up_pacific ? new Date(u.signed_up_pacific).toLocaleDateString() : "",
			u.activity_status,
			u.pwa_install,
			u.is_subscribed,
			u.total_user_msgs, u.chat_msgs, u.voice_msgs, u.video_msgs,
			u.used_voice, u.used_video, u.voice_calls, u.video_calls,
			u.voice_min, u.video_min,
			u.msgs_24h, u.msgs_48h, u.msgs_7d,
			u.last_msg_pacific || "", u.last_call_pacific || "", u.tour_shown ? "yes" : "no",
		]);
		const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "users_stats.csv";
		a.click();
		URL.revokeObjectURL(url);
	};

	// DataTables columns: `data` picks the field, `render` formats it as HTML.
	const columns = useMemo(() => [
		{ title: "#", data: "rank" },
		{
			title: "Cohort", data: "cohort",
			render: (v) => {
				const bg = v === "Original 8" ? "#60a5fa22" : "#4ade8022";
				const color = v === "Original 8" ? "#60a5fa" : "#4ade80";
				return `<span style="font-size:0.75em;padding:2px 8px;border-radius:10px;background:${bg};color:${color};">${v || "—"}</span>`;
			},
		},
		{
			title: "Name", data: null,
			render: (u) => [u.first_name, u.last_name].filter(Boolean).join(" ") || "—",
		},
		{ title: "Email", data: "email", render: (v) => v || "—" },
		{
			title: "Signed up", data: "signed_up_pacific",
			render: (v) => (v ? fmtDate(v) : "—"),
		},
		{
			title: "Status", data: "activity_status",
			render: (v) => `<span style="font-size:0.85em;padding:2px 6px;border-radius:6px;background:${statusColor(v)}18;color:${statusColor(v)};">${v || "—"}</span>`,
		},
		{ title: "Pwa Install", data: "pwa_install", render: (v) => (v ? "Yes" : "No") },
		{ title: "Subscribed", data: "is_subscribed", render: (v) => (v ? "Yes" : "No") },
		{ title: "Total msgs", data: "total_user_msgs", render: (v) => v ?? 0, className: "dt-right" },
		{ title: "Chat", data: "chat_msgs", render: (v) => v ?? 0, className: "dt-right" },
		{ title: "Voice msgs", data: "voice_msgs", render: (v) => v ?? 0, className: "dt-right" },
		{ title: "Video msgs", data: "video_msgs", render: (v) => v ?? 0, className: "dt-right" },
		{ title: "Voice", data: "used_voice", render: (v) => (v === "yes" ? "✓" : "—"), className: "dt-center" },
		{ title: "Video", data: "used_video", render: (v) => (v === "yes" ? "✓" : "—"), className: "dt-center" },
		{ title: "Voice calls", data: "voice_calls", render: (v) => v ?? 0, className: "dt-right" },
		{ title: "Video calls", data: "video_calls", render: (v) => v ?? 0, className: "dt-right" },
		{ title: "Voice min", data: "voice_min", render: (v) => fmtMin(v), className: "dt-right" },
		{ title: "Video min", data: "video_min", render: (v) => fmtMin(v), className: "dt-right" },
		{ title: "24h", data: "msgs_24h", render: (v) => v ?? 0, className: "dt-right" },
		{ title: "48h", data: "msgs_48h", render: (v) => v ?? 0, className: "dt-right" },
		{ title: "7d", data: "msgs_7d", render: (v) => v ?? 0, className: "dt-right" },
		{ title: "Last msg", data: "last_msg_pacific", render: (v) => (v ? fmtDate(v) : "—") },
		{ title: "Last call", data: "last_call_pacific", render: (v) => (v ? fmtDate(v) : "—") },
		{ title: "Last Activity", data: "last_activity_pacific", render: (v) => (v ? fmtDate(v) : "—") },
		{ title: "Tour", data: "tour_shown", render: (v) => (v ? "✓" : "—"), className: "dt-center" },
	], []);

	return (
		<Box p={3} sx={CHART_STYLE}>
			<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
				<Box>
					<Typography sx={{ ...CARD_TITLE, mb: 0 }}>User stats</Typography>
					<Typography sx={CARD_SUB}>{users.length} results</Typography>
				</Box>
				<Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
					<Select
						size="small"
						sx={{ color: "#1F2430", backgroundColor: "#F7F8FC", minWidth: 140,
							"& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E4EE" } }}
						value={sortBy}
						onChange={(e) => setSortBy(e.target.value)}
					>
						<MenuItem value={"signed_up_pacific"}>Sort: Signed up</MenuItem>
						<MenuItem value={"last_activity_pacific"}>Sort: Last activity</MenuItem>
					</Select>
					<Select
						size="small"
						value={selectedCreator}
						onChange={(e) => setSelectedCreator(e.target.value)}
						sx={{ color: "#1F2430", backgroundColor: "#F7F8FC", minWidth: 140,
							"& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E4EE" } }}
					>
						<MenuItem value="all">All creators</MenuItem>
						{(creatorIdList || []).map((c) => (
							<MenuItem key={c?.username} value={c?.username}>{c?.first_name} {c?.last_name}</MenuItem>
						))}
					</Select>
					<TextField
						size="small"
						placeholder="Search name, email"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						sx={{ "& .MuiOutlinedInput-root": { color: "#1F2430", backgroundColor: "#F7F8FC" },
							"& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E4EE" } }}
					/>
					<Button
						size="small"
						startIcon={<FiDownload />}
						onClick={handleExportCsv}
						sx={{
							px: 3, py: 1, minWidth: 110, borderRadius: "14px", textTransform: "none",
							fontWeight: 600, color: "#fff",
							background: "linear-gradient(145deg, #f43f8d, #a855f7)",
							boxShadow: "0 6px 16px rgba(168,85,247,0.28)",
							transition: "all .25s ease",
							"&:hover": { transform: "translateY(-2px) scale(1.02)", boxShadow: "0 10px 22px rgba(168,85,247,0.38)" },
							"&:active": { transform: "scale(.98)", boxShadow: "0 4px 10px rgba(168,85,247,0.25)" },
						}}
					>
						Export CSV
					</Button>
				</Box>
			</Box>

			{loading ? (
				<Typography color="#9CA3AF" py={3} textAlign="center">Loading...</Typography>
			) : (
				<Box
					sx={{
						overflowX: "auto",
						"& table.dataTable th, & table.dataTable td": {
							whiteSpace: "nowrap", borderColor: "#EEF0F5", padding: "10px 12px",
						},
						"& table.dataTable th": { color: "#8B93A1", fontWeight: 600 },
						"& table.dataTable td": { color: "#1F2430" },
						"& .dt-right": { textAlign: "right" },
						"& .dt-center": { textAlign: "center" },
						"& .dataTables_wrapper .dataTables_filter": { display: "none" }, // using our own search box instead

						// Bottom bar: entries-per-page + info text + pagination, laid out together
						"& .dt-bottom": {
							display: "flex",
							flexWrap: "wrap",
							justifyContent: "space-between",
							alignItems: "center",
							gap: 12,
							padding: "12px 4px 4px",
							fontSize: 13,
							color: "#8B93A1",
						},

						// Fix the double-arrow on the "entries per page" select: the browser's
						// native dropdown arrow was rendering underneath DataTables' own arrow.
						// Turning off native appearance and drawing exactly one custom chevron instead.
						"& .dt-length select": {
							appearance: "none",
							WebkitAppearance: "none",
							MozAppearance: "none",
							borderRadius: "8px",
							border: "1px solid #E2E4EE",
							padding: "4px 26px 4px 10px",
							backgroundColor: "#F7F8FC",
							// backgroundImage:
							// 	"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%238B93A1' stroke-width='1.5' fill='none' fill-rule='evenodd'/></svg>\")",
							backgroundRepeat: "no-repeat",
							backgroundPosition: "right 8px center",
							color: "#1F2430",
							marginInline: 4,
                            height: "50px",
                            width: "60px",
						},

						"& .dt-paging .dt-paging-button": {
							borderRadius: "8px", border: "1px solid #E2E4EE", margin: "0 2px",
							padding: "4px 10px", color: "#6B7280", backgroundColor: "#fff",
						},
						"& .dt-paging .dt-paging-button.current": {
							background: "linear-gradient(145deg, #f43f8d, #a855f7)",
							color: "#fff", border: "none", fontWeight: 600,
						},
						"& .dt-paging .dt-paging-button.disabled": { color: "#D1D5DB" },
					}}
				>
					<DataTable
						ref={tableRef}
						data={users}
						columns={columns}
						className="display"
						options={{
							dom: 'rt<"dt-bottom"lip>', // table, then a bottom bar holding length + info + pagination
							paging: true,
							searching: true,
							lengthMenu: [10, 25, 50, 100],
							pageLength: 10,
							order: [],
							responsive: true,
						}}
					/>
				</Box>
			)}
		</Box>
	);
}

export default AllAccounts;